/**
 * Cross-tab auth synchronization channel (singleton).
 *
 * Uses the **native** BroadcastChannel API for messaging and the
 * `broadcast-channel` npm package solely for leader election so that only
 * one tab at a time runs the periodic session-check interval.
 *
 * Message shape:
 *   { type: 'ACCOUNT_SWITCHED' | 'LOGGED_OUT', tabId: string, clientId?: string }
 */
import { BroadcastChannel as BroadcastChannelPolyfill } from 'broadcast-channel'
import { createLeaderElection } from 'broadcast-channel'
import logger from './logger'
import { sanitizeLogMessage } from './sanitize'

// ─── Constants ───────────────────────────────────────────────────────────────
// Separate channel names so leader-election doesn't crash on app-level auth messages.
// (The leader-election listener expects only its own internal message types.)
const CHANNEL_NAME = 'auth'
const ELECTION_CHANNEL_NAME = 'auth_leader_election'
const TAB_ID_KEY = 'auth_tab_id'

const safeSessionStorage = {
  getItem(key) {
    try {
      return window.sessionStorage?.getItem(key)
    } catch {
      return null
    }
  },
  setItem(key, value) {
    try {
      window.sessionStorage?.setItem(key, value)
    } catch {
      /* storage unavailable */
    }
  },
}

const hasNativeBroadcastChannel = () =>
  typeof window !== 'undefined' && typeof window.BroadcastChannel === 'function'

// ─── Per-tab ID (survives re-renders, not tab close) ────────────────────────
const getTabId = () => {
  let id = safeSessionStorage.getItem(TAB_ID_KEY)
  if (!id) {
    id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
    safeSessionStorage.setItem(TAB_ID_KEY, id)
  }
  return id
}

// ─── Singleton instances ─────────────────────────────────────────────────────
let _nativeChannel = null // native BroadcastChannel for messaging
let _electionChannel = null // broadcast-channel instance for leader election
let _elector = null
const _listeners = new Set()

/**
 * Lazily create the native BroadcastChannel (messaging) and the
 * broadcast-channel instance (leader election).  Both are created once and
 * reused for the lifetime of the page.
 */
const ensureChannel = () => {
  if (!_nativeChannel && hasNativeBroadcastChannel()) {
    _nativeChannel = new window.BroadcastChannel(CHANNEL_NAME)

    // Fan-out incoming messages to all registered listeners
    _nativeChannel.onmessage = (event) => {
      const msg = event.data
      _listeners.forEach((fn) => {
        try {
          fn(msg)
        } catch (err) {
          logger.error('authChannel listener error:', sanitizeLogMessage(err))
        }
      })
    }
  }

  if (!_electionChannel) {
    try {
      _electionChannel = new BroadcastChannelPolyfill(ELECTION_CHANNEL_NAME)
      _elector = createLeaderElection(_electionChannel)
    } catch (err) {
      logger.warn('authChannel leader election unavailable:', sanitizeLogMessage(err))
      _electionChannel = null
      _elector = null
    }
  }
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Broadcast an auth event to all other tabs.
 * @param {'ACCOUNT_SWITCHED' | 'LOGGED_OUT'} type
 * @param {Record<string, unknown>=} extra  – merged into the message payload
 */
const broadcast = (type, extra = {}) => {
  ensureChannel()
  if (!_nativeChannel) return
  try {
    _nativeChannel.postMessage({ type, tabId: getTabId(), ...extra })
  } catch (err) {
    logger.error('authChannel broadcast error:', sanitizeLogMessage(err))
  }
}

/**
 * Subscribe a listener.  Returns an unsubscribe function.
 * @param {(msg: {type: string, tabId: string, [key: string]: unknown}) => void} fn
 * @returns {() => void}
 */
const subscribe = (fn) => {
  ensureChannel()
  _listeners.add(fn)
  return () => _listeners.delete(fn)
}

/**
 * Request leadership.  Resolves when this tab becomes leader (may be
 * immediate if no other leader exists).
 * @returns {Promise<void>}
 */
const awaitLeadership = async () => {
  ensureChannel()
  if (!_elector) return
  return _elector.awaitLeadership()
}

/**
 * Whether this tab is currently the elected leader.
 * @returns {boolean}
 */
const isLeader = () => {
  return _elector ? !!_elector.isLeader : true
}

/**
 * Tear down both channels.  Safe to call multiple times.
 */
const close = () => {
  if (_nativeChannel) {
    try {
      _nativeChannel.close()
    } catch {
      /* already closed */
    }
    _nativeChannel = null
  }
  if (_electionChannel) {
    try {
      _elector?.die()
      _electionChannel.close()
    } catch {
      /* already closed */
    }
    _electionChannel = null
    _elector = null
  }
  _listeners.clear()
}

export { getTabId, broadcast, subscribe, awaitLeadership, isLeader, close }
